/**
 * Guided knowledge-capture interview templates (chat-first; voice sessions planned).
 * `systemPrompt` + `completionCriteria` are sent to the model as context (not shown in the UI).
 */
export const CAPTURE_TEMPLATES = [
    {
        id: 'weekly-handoff',
        title: 'Weekly handoff',
        description: 'Short recurring capture of progress, blockers, and decisions from the past week.',
        cadence: 'Weekly',
        systemPrompt:
            'You are a knowledge-capture interviewer for a weekly handoff. ' +
            'Sequence: (1) Deepen accomplishments until you know specific features, systems/APIs, approach, and collaborators. ' +
            '(2) Blockers. (3) Decisions. (4) Next week. Never skip step 1 while answers are still vague.',
        completionCriteria:
            'You have enough when you can name: what they built (feature + product/API), how they built it, who was involved, ' +
            'blockers (or none), key decisions with rationale, and concrete next-week plans. Then YOU summarize and stop — never ask them to summarize.',
        openingMessage:
            'Welcome to your weekly handoff. What were the most important things you worked on since our last session?'
    },
    {
        id: 'project-retrospective',
        title: 'Project retrospective',
        description: 'Structured look-back on a project: outcomes, lessons, and reusable knowledge.',
        cadence: 'Per project',
        systemPrompt:
            'Interview about a project. First establish: project name, goal, timeline, team, tech stack, and actual outcome. ' +
            'Then successes, failures, surprises, and lessons. One question at a time; probe vague answers.',
        completionCriteria:
            'You have enough when you know: project name/goal, outcome, success, challenge, stakeholders, tech, and lessons. Minimum 8 expert answers.',
        openingMessage: 'Which project are we capturing today, and what was its primary goal?'
    },
    {
        id: 'expertise-interview',
        title: 'Expertise interview',
        description: 'Draw out tacit know-how: how work really gets done, tools, shortcuts, and judgment calls.',
        cadence: 'Periodic',
        systemPrompt:
            'Conduct a tacit-knowledge interview. Get: the exact process/skill, tools, step-by-step workflow, exceptions, ' +
            'quality checks, and escalation contacts. Use real examples. Probe until a newcomer could follow it.',
        completionCriteria:
            'You have enough when someone else could execute the process: steps, tools, exceptions, and who to ask. Minimum 9 expert answers.',
        openingMessage:
            'What area of your work should we document today — a process, tool, or situation you are the go-to person for?'
    },
    {
        id: 'transition-capture',
        title: 'Transition capture',
        description: 'Before a role change or leave: responsibilities, contacts, and unfinished work.',
        cadence: 'As needed',
        systemPrompt:
            'Capture knowledge before a role transition. Get: role scope, daily responsibilities, named contacts, ' +
            'in-flight work with status, documentation locations, and risks if unavailable. Probe for names and systems.',
        completionCriteria:
            'You have enough when a successor knows: responsibilities, named contacts, open work, doc locations, and top risks. Minimum 8 expert answers.',
        openingMessage: 'What role or scope are you transitioning, and who will need this knowledge after you?'
    }
];

export function getCaptureTemplate(id) {
    return CAPTURE_TEMPLATES.find((t) => t.id === id) || null;
}
